import { useState } from 'react'

import { toast } from 'react-hot-toast'

import {
  ICountry,
  IMapping,
  IPartner,
  IPaymentMode,
  IPlatform,
  IProduct,
  IProvider,
} from 'pages/PayCms/MasterTables/utils/interface'
import { get, post, put } from 'utils/apiClient'
import { IApiResponse, PageInterface } from 'utils/apiInterfaces'
import apiEndPoints from 'utils/constants/apiEndpoints'

export const useFetchPlatforms = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [platformsData, setPlatformsData] =
    useState<IApiResponse<IPlatform[]>>()

  const fetchPlatforms = async ({ page, rowsPerPage }: PageInterface) => {
    setIsLoading(true)
    try {
      const resp: IApiResponse<IPlatform[]> = await get(
        `${apiEndPoints.MASTER_PLATFORM}?page=${page}&limit=${rowsPerPage}`
      )
      setPlatformsData(resp)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  return { isLoading, platformsData, fetchPlatforms }
}

export const useFetchPlatformsList = () => {
  const [platformsData, setPlatformsData] =
    useState<IApiResponse<IPlatform[]>>()

  const fetchPlatforms = async () => {
    try {
      const resp: IApiResponse<IPlatform[]> = await get(
        `${apiEndPoints.MASTER_PLATFORM}?page=1&limit=100`
      )
      setPlatformsData(resp)
    } catch (error) {
      /* empty */
    }
  }

  return { platformsData, fetchPlatforms }
}

export const useFetchProducts = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [productsData, setProductsData] = useState<IApiResponse<IProduct[]>>()

  const fetchProducts = async ({ page, rowsPerPage }: PageInterface) => {
    setIsLoading(true)
    try {
      const resp: IApiResponse<IProduct[]> = await get(
        `${apiEndPoints.MASTER_PRODUCT}?page=${page}&limit=${rowsPerPage}`
      )
      setProductsData(resp)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  return { isLoading, productsData, fetchProducts }
}

export const useUpdateMasterProduct = () => {
  const updateProduct = async (productId = '', reqObj = {}) => {
    try {
      const res: IApiResponse<IProduct> = await put(
        `${apiEndPoints.MASTER_PRODUCT}/${productId}`,
        reqObj
      )
      toast.success(res?.message)
    } catch (error) {
      // Intentionally empty catch block
    }
  }
  return updateProduct
}

export const useCreateMasterProduct = () => {
  const createProduct = async (reqObj = {}) => {
    try {
      const res: IApiResponse<IProduct> = await post(
        `${apiEndPoints.MASTER_PRODUCT}`,
        reqObj
      )
      toast.success(res?.message)
    } catch (error) {
      // Intentionally empty catch block
    }
  }
  return createProduct
}

export const useUpdateMasterPlatform = () => {
  const updatePlatform = async (platformId = '', reqObj = {}) => {
    try {
      const res: IApiResponse<IPlatform> = await put(
        `${apiEndPoints.MASTER_PLATFORM}/${platformId}`,
        reqObj
      )
      toast.success(res?.message)
    } catch (error) {
      // Intentionally empty catch block
    }
  }
  return updatePlatform
}

export const useCreateMasterPlatform = () => {
  const createPlatform = async (reqObj = {}) => {
    try {
      const res: IApiResponse<IProduct> = await post(
        `${apiEndPoints.MASTER_PLATFORM}`,
        reqObj
      )
      toast.success(res?.message)
    } catch (error) {
      // Intentionally empty catch block
    }
  }
  return createPlatform
}

export const useUpdateMasterCountry = () => {
  const updateCountry = async (countryId = '', reqObj = {}) => {
    try {
      const res: IApiResponse<ICountry> = await put(
        `${apiEndPoints.MASTER_COUNTRIES}/${countryId}`,
        reqObj
      )
      toast.success(res?.message)
    } catch (error) {
      // Intentionally empty catch block
    }
  }
  return updateCountry
}

export const useCreateMasterCountry = () => {
  const createCountry = async (reqObj = {}) => {
    try {
      const res: IApiResponse<ICountry> = await post(
        `${apiEndPoints.MASTER_COUNTRIES}`,
        reqObj
      )
      toast.success(res?.message)
    } catch (error) {
      // Intentionally empty catch block
    }
  }
  return createCountry
}

export const useFetchCountries = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [countriesData, setCountriesData] = useState<IApiResponse<ICountry[]>>()

  const fetchCountries = async ({ page, rowsPerPage }: PageInterface) => {
    setIsLoading(true)
    try {
      const resp: IApiResponse<ICountry[]> = await get(
        `${apiEndPoints.MASTER_COUNTRIES}?page=${page}&limit=${rowsPerPage}`
      )
      setCountriesData(resp)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  return { isLoading, countriesData, fetchCountries }
}

export const useFetchProviders = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [providersData, setProvidersData] =
    useState<IApiResponse<IProvider[]>>()

  const fetchProviders = async ({ page, rowsPerPage }: PageInterface) => {
    setIsLoading(true)
    try {
      const resp: IApiResponse<IProvider[]> = await get(
        `${apiEndPoints.PROVIDER_MASTER}?page=${page}&limit=${rowsPerPage}`
      )

      setProvidersData(resp)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  return { isLoading, providersData, fetchProviders }
}

export const useCreateProviders = () => {
  const createProviders = async (postData: any) => {
    const resp: IApiResponse<IProvider[]> = await post(
      apiEndPoints.PROVIDER_MASTER,
      postData
    )
    toast.success(resp?.message)
  }

  return { createProviders }
}

export const useUpdateProviders = () => {
  const updateProviders = async (putData: any, resourceId: any) => {
    const resp: IApiResponse<IProvider[]> = await put(
      `${apiEndPoints.PROVIDER_MASTER}/${resourceId}`,
      putData
    )
    toast.success(resp?.message)
  }

  return { updateProviders }
}

export const useFetchMappings = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [mappingsData, setMappingsData] = useState<any>()

  const fetchMappings = async (page: any, providerId: any) => {
    setIsLoading(true)
    try {
      const resp: any = await get(
        `${apiEndPoints.MAPPING_MASTER}?page=${page.pageIndex}&limit=${
          page.pageSize
        }${providerId !== undefined ? `&providerId=${providerId}` : ''}`
      )

      setMappingsData(resp)
      setIsLoading(false)
    } catch (error) {
      setMappingsData([])
      setIsLoading(false)
    }
  }

  return { isLoading, mappingsData, fetchMappings }
}

export const useCreateMappings = () => {
  const createMappings = async (postData: any) => {
    const resp: IApiResponse<IMapping[]> = await post(
      apiEndPoints.MAPPING_MASTER,
      postData
    )
    toast.success(resp?.message)
  }

  return { createMappings }
}

export const useUpdateMappings = () => {
  const updateMappings = async (putData: any, resourceId: any) => {
    const resp: IApiResponse<IMapping[]> = await put(
      `${apiEndPoints.MAPPING_MASTER}/${resourceId}`,
      putData
    )
    toast.success(resp?.message)
  }

  return { updateMappings }
}

export const useFetchPaymentModes = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [paymentModesData, setPaymentModesData] =
    useState<IApiResponse<IPaymentMode[]>>()

  const fetchPaymentModes = async ({ page, rowsPerPage }: PageInterface) => {
    setIsLoading(true)

    try {
      const resp: IApiResponse<IPaymentMode[]> = await get(
        `${apiEndPoints.PAYMENT_MODE_MASTER}?page=${page}&limit=${rowsPerPage}`
      )
      setPaymentModesData(resp)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  return { isLoading, paymentModesData, fetchPaymentModes }
}

export const useCreatePaymentModes = () => {
  const createPaymentModes = async (postData: any) => {
    const resp: IApiResponse<IPaymentMode[]> = await post(
      apiEndPoints.PAYMENT_MODE_MASTER,
      postData
    )
    toast.success(resp?.message)
  }

  return { createPaymentModes }
}

export const useUpdatePaymentModes = () => {
  const updatePaymentModes = async (putData: any, resourceId: any) => {
    const resp: IApiResponse<IPaymentMode[]> = await put(
      `${apiEndPoints.PAYMENT_MODE_MASTER}/${resourceId}`,
      putData
    )
    toast.success(resp?.message)
  }

  return { updatePaymentModes }
}

export const useFetchTableByParams = () => {
  const [searchData, setSearchData] = useState<any>()

  const fetchtableByParams = async (
    searchText?: string,
    tableType?: string,
    providerId?: string
  ) => {
    try {
      const response: any = await get(
        `${
          apiEndPoints.MASTER_TABLE_SEARCH
        }?searchText=${searchText}&section=${tableType}&pagination=false${
          providerId !== undefined ? `&providerId=${providerId}` : ''
        }`
      )
      setSearchData(response.data)
    } catch (error) {
      return null
    }
    return null
  }

  return { fetchtableByParams, searchData }
}

export const useFetchPartners = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [partnersData, setPartnersData] = useState<IApiResponse<IPartner[]>>()

  const fetchPartners = async ({ page, rowsPerPage }: PageInterface) => {
    setIsLoading(true)

    try {
      const resp: IApiResponse<IPartner[]> = await get(
        `${apiEndPoints.PARTNERS_LIST}?page=${page}&limit=${rowsPerPage}`
      )
      setPartnersData(resp)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }
  return { isLoading, partnersData, fetchPartners }
}

export const useCreatePartners = () => {
  const createPartners = async (payload: {
    name: string
    isActive: boolean
  }) => {
    const resp: IApiResponse<IPartner> = await post(
      apiEndPoints.PARTNERS_LIST,
      payload
    )
    toast.success(resp?.message)
  }

  return createPartners
}

export const useUpdatePartner = () => {
  const updatePartner = async (
    id: string,
    reqObj: { name: string; isActive: boolean }
  ) => {
    const res: IApiResponse<IPartner> = await put(
      `${apiEndPoints.PARTNERS_LIST}/${id}`,
      reqObj
    )
    toast.success(res?.message)
  }
  return updatePartner
}
